/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, December 5th 2021, 4:35:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import { useControlledInput } from "ui-widgets/forms";
import {
  APIFormInputErrorWrapper,
  CustomInputValidationErrorWrapper,
  HelperTextInputWrapper,
} from "ui-widgets/forms/FormInputWrappers";
import { GenericAPIMany2ManyFormInputProps } from "ui-widgets/forms/types";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { useAPIFetchList } from "lib/api";
import { PrimaryKeyValue } from "lib/types";

export const APIMany2ManyCheckBoxFormInput = <P, R>(props: GenericAPIMany2ManyFormInputProps<P, R>) => {
  const {
    field: { onChange, value },
  } = useControlledInput<P>(props);

  const selectedItemIds = (value ?? []) as PrimaryKeyValue<R>[];

  const OnChangeSelectedItemIds = (selectedItemIds: PrimaryKeyValue<R>[]) => onChange(selectedItemIds);

  const {
    items: apiItemList,
    isMakingAPIRequest,
    isAPIError,
    httpResponseStatusCode,
  } = useAPIFetchList<R>({ ...props, makeRequestOnInitialMount: true }); // Creating fetch list args from props since they are part of APIRelatedObjectSelectProps. This can probably be improved to dynamically destruct all fetchList args only without including irrelvant props as is done here.

  const onCheckItem = (itemId: PrimaryKeyValue<R>) => {
    if (selectedItemIds.includes(itemId)) {
      OnChangeSelectedItemIds(selectedItemIds.filter((v) => v !== itemId));
    } else {
      OnChangeSelectedItemIds([...selectedItemIds, itemId]);
    }
  };

  const filteredItems = props.getFilteredItems ? props.getFilteredItems(apiItemList) : apiItemList;

  return (
    <APIListProgressWrapper
      showLoader={isMakingAPIRequest}
      isAPIError={isAPIError}
      items={filteredItems}
      httpResponseStatusCode={httpResponseStatusCode}
      noDataMessage={props.noDataMessage}
    >
      <APIFormInputErrorWrapper {...props}>
        <CustomInputValidationErrorWrapper {...props}>
          <HelperTextInputWrapper helperText={props.helperText}>
            {props.label && <FormLabel component="h4">{props.label}</FormLabel>}

            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ overflow: "auto" }}>
              {filteredItems.map((item, index) => {
                return (
                  <Grid item xs={2} sm={4} md={4} key={index}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedItemIds.includes(item?.[props.itemIdProperty] as PrimaryKeyValue<R>)}
                          onChange={() => {}}
                          name={item?.[props.itemIdProperty] as unknown as string}
                        />
                      }
                      label={
                        props.getOptionComponent?.(item, {
                          isSelected: selectedItemIds.includes(item?.[props.itemIdProperty]),
                        }) ?? props.getOptionLabel?.(item)
                      }
                      onClick={() => onCheckItem(item?.[props.itemIdProperty] as PrimaryKeyValue<R>)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </HelperTextInputWrapper>
        </CustomInputValidationErrorWrapper>
      </APIFormInputErrorWrapper>
    </APIListProgressWrapper>
  );
};
